/**
 * BulkPayDialog — buyer-facing "pay N invoices in one shot" flow.
 *
 * Called from BuyerInvoicesList when the buyer ticks 1+ payable rows
 * and clicks "Pay Selected". Wallet-only for v1, cross-seller allowed.
 *
 * Flow:
 *   1. Review screen — list each invoice (seller / code / due / amount)
 *      with running total + wallet-balance sanity check.
 *   2. Owner SMS approval — single OTP for the entire batch via the
 *      reusable PaymentApprovalModal (action="bulk_pay").
 *   3. Submit — POST /api/invoices/bulk-pay with the approval_token.
 *   4. Result — toast + close. The parent reloads the list.
 *
 * Caller:
 *   <BulkPayDialog
 *      open={open} onClose={…}
 *      invoices={selectedInvoices}     // pre-filtered to payable rows
 *      walletBalance={wallet.balance}
 *      onSuccess={(result) => {...}}
 *   />
 */
import { useEffect, useMemo, useState } from "react";
import {
  Wallet, Loader2, AlertTriangle, Receipt, Info, CheckCircle2, XCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { money, formatErr } from "../lib/api";
import PaymentApprovalModal from "./PaymentApprovalModal";

const PAYABLE = ["sent", "outstanding", "paying", "credit_terms", "overdue"];

export default function BulkPayDialog({
  open, onClose, invoices = [], walletBalance = 0, onSuccess,
}) {
  const [busy, setBusy] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);

  useEffect(() => {
    if (!open) { setBusy(false); setApprovalOpen(false); }
  }, [open]);

  // Only invoices that actually CAN be paid — defensive even though the
  // checkbox in the list already gates this.
  const payable = useMemo(() => {
    return (invoices || []).filter(i => {
      if (!PAYABLE.includes(i.status)) return false;
      const due = Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0));
      return due > 0;
    });
  }, [invoices]);

  const total   = useMemo(
    () => payable.reduce((s, i) => s + Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0)), 0),
    [payable],
  );
  const overWallet = total > walletBalance;
  const canPay     = payable.length > 0 && !overWallet && !busy;

  // Cross-seller breakdown for the disclosure footer.
  const sellersCount = useMemo(() => {
    const set = new Set(payable.map(i => i.seller_id).filter(Boolean));
    return set.size;
  }, [payable]);

  const submitWithApproval = async (approval_token) => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/bulk-pay", {
        invoice_ids:    payable.map(i => i.id),
        approval_token,
      });
      // Mercury-style summary toast — show partial-success when relevant.
      if (data.failed_count > 0) {
        toast.warning(
          `Paid ${data.paid_count} of ${data.paid_count + data.failed_count} · ${money(data.total_paid_usd)}` +
          ` (${data.failed_count} failed — see details).`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Paid ${data.paid_count} invoice${data.paid_count === 1 ? "" : "s"} · ${money(data.total_paid_usd)}`);
      }
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || formatErr(detail) || "Bulk payment failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-lg" data-testid="bulk-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-4 h-4"/>
              Pay {payable.length} invoice{payable.length === 1 ? "" : "s"}
            </DialogTitle>
            <div className="text-[12px] text-foreground/60 mt-1">
              All paid from your wallet · {sellersCount} seller{sellersCount === 1 ? "" : "s"}
            </div>
          </DialogHeader>

          {payable.length === 0 ? (
            <div className="py-6 text-center text-foreground/55 text-[13px]">
              None of the selected invoices are currently payable.
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-border overflow-hidden max-h-80 overflow-y-auto">
                {payable.map(i => {
                  const due = Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0));
                  return (
                    <div key={i.id}
                          className="px-4 py-2.5 flex items-center gap-3 border-b border-border last:border-b-0 text-[13px]"
                          data-testid={`bulk-row-${i.code}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          <span className="mono text-[12px]">{i.code}</span>
                          <span className="text-foreground/55 text-[11.5px]">·</span>
                          <span className="truncate">{i.seller_name || "—"}</span>
                        </div>
                        {i.due_date && (
                          <div className="text-[10.5px] text-foreground/55 mt-0.5">
                            Due {new Date(i.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </div>
                        )}
                      </div>
                      <div className="display tabular-nums shrink-0">{money(due)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl bg-secondary/40 border border-border px-4 py-3 flex items-center justify-between mt-1">
                <div className="text-[12px] mono uppercase tracking-[0.15em] text-foreground/55">
                  Total
                </div>
                <div className="display text-2xl tabular-nums" data-testid="bulk-total">
                  {money(total)}
                </div>
              </div>

              <div className="flex items-center justify-between text-[11.5px] text-foreground/55 px-1">
                <span className="inline-flex items-center gap-1.5">
                  <Wallet className="w-3 h-3"/>
                  Wallet: <span className="font-medium text-foreground/80">{money(walletBalance)}</span>
                </span>
                {overWallet ? (
                  <span className="inline-flex items-center gap-1.5 text-rose-700">
                    <AlertTriangle className="w-3 h-3"/>
                    {money(total - walletBalance)} short — top up first
                  </span>
                ) : (
                  <span>After: <span className="font-medium text-foreground/75">{money(walletBalance - total)}</span></span>
                )}
              </div>

              <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5 text-[11.5px] text-sky-900 leading-relaxed flex items-start gap-2">
                <Info className="w-3 h-3 mt-0.5 shrink-0"/>
                <span>
                  Your account owner will approve this batch with a single 6-digit code.
                  Each invoice is settled individually — partial success is fine
                  (e.g. if one invoice gets voided mid-batch, the rest still pay).
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1 border-t border-border">
                <Button type="button" variant="outline" className="rounded-full"
                  onClick={onClose} data-testid="bulk-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={() => setApprovalOpen(true)}
                  disabled={!canPay}
                  data-testid="bulk-pay-submit"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                  {busy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Paying…</>
                    : <>Pay {money(total)}</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PaymentApprovalModal
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        action="bulk_pay"
        amount={total}
        title={`Approve ${money(total)} bulk payment`}
        description={`Pays ${payable.length} invoice${payable.length === 1 ? "" : "s"} from your wallet.`}
        onApproved={submitWithApproval}
      />
    </>
  );
}
